/**
 * Client-side mirror of the condition report windows.
 *
 * The server in functions/conditionReports.js is the authority — it rejects
 * anything outside the window regardless of what the browser believes. This
 * copy exists only so the UI can show the right thing without a round trip.
 * If the timings change, change them in both places.
 */

import { parseLocalDate } from './dateUtils';

export const PICKUP = 'pickup';
export const RETURN = 'return';
export const PHASES = [PICKUP, RETURN];

export const PICKUP_GRACE_HOURS = 24;
export const RETURN_GRACE_HOURS = 48;
export const AUTO_CONFIRM_HOURS = 24;
export const MAX_PHOTOS_PER_REPORT = 12;

const HOUR_MS = 60 * 60 * 1000;

export function getPhaseWindow(rental, phase) {
  const start = parseLocalDate(rental?.rentalStartDate);
  const end = parseLocalDate(rental?.rentalEndDate);
  if (!start || !end) return null;

  if (phase === PICKUP) {
    return {
      opensAt: null,
      closesAt: new Date(start.getTime() + PICKUP_GRACE_HOURS * HOUR_MS),
    };
  }

  const endOfDay = new Date(end.getTime());
  endOfDay.setHours(23, 59, 59, 999);
  return {
    opensAt: new Date(end.getTime()),
    closesAt: new Date(endOfDay.getTime() + RETURN_GRACE_HOURS * HOUR_MS),
  };
}

export function isWindowOpen(rental, phase, now = new Date()) {
  const window = getPhaseWindow(rental, phase);
  if (!window) return false;
  if (window.opensAt && now < window.opensAt) return false;
  return now <= window.closesAt;
}

export function getWindowState(rental, phase, now = new Date()) {
  const window = getPhaseWindow(rental, phase);
  if (!window) return 'unavailable';
  if (window.opensAt && now < window.opensAt) return 'not_yet';
  return now <= window.closesAt ? 'open' : 'closed';
}

export function describeWindow(rental, phase, now = new Date()) {
  const state = getWindowState(rental, phase, now);
  const window = getPhaseWindow(rental, phase);

  if (state === 'unavailable') return 'Rental dates are missing.';
  if (state === 'not_yet') {
    return 'Opens on the last day of the rental.';
  }
  if (state === 'closed') {
    return phase === PICKUP
      ? 'Closed — pickup photos could be added until 24 hours after the rental started.'
      : 'Closed — return photos could be added until 48 hours after the rental ended.';
  }

  const closes = window.closesAt;
  const hoursLeft = Math.max(0, Math.round((closes - now) / HOUR_MS));
  if (hoursLeft <= 48) {
    return `Open — about ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'} left to add photos.`;
  }
  return 'Open for photos.';
}

export function roleFor(rental, userId) {
  if (!rental || !userId) return null;
  if (rental.ownerId === userId) return 'owner';
  if (rental.renterId === userId) return 'renter';
  return null;
}

export function counterpartRole(role) {
  return role === 'owner' ? 'renter' : 'owner';
}

/**
 * Whether this user still owes an answer: the other party added photos and
 * this user has neither confirmed nor flagged them.
 */
export function needsMyResponse(report, myRole) {
  if (!report || !(report.photos || []).length || !myRole) return false;
  if (report.status === 'confirmed' || report.status === 'disputed') return false;

  const iContributed = report.photos.some((p) => p.uploadedByRole === myRole);
  if (iContributed) return false;

  return myRole === 'owner' ? !report.ownerConfirmed : !report.renterConfirmed;
}

export function describeStatus(report) {
  if (!report || !(report.photos || []).length) return 'No photos yet';
  switch (report.status) {
    case 'confirmed':
      return report.autoConfirmed ? 'Agreed by default — no response' : 'Agreed by both';
    case 'disputed':
      return 'Mismatch reported';
    case 'awaiting_confirmation':
      return 'Waiting on the other person';
    default:
      return 'In progress';
  }
}

export function statusTone(report) {
  if (!report || !(report.photos || []).length) return 'neutral';
  if (report.status === 'disputed') return 'warn';
  if (report.status === 'confirmed') return 'good';
  return 'pending';
}

/** A claim needs both handoffs documented. */
export function claimEvidenceReady(pickupReport, returnReport) {
  const usable = (r) => Boolean(r && (r.photos || []).length);
  return usable(pickupReport) && usable(returnReport);
}
